<template>
  <div class="customer__form modal modal--default">
    <div class="modal__panel">
      <div class="modal__logo-wrapper">
        <div class="modal__logo"></div>
      </div>

      <div class="modal__main-icon modal__main-icon--private"></div>
      <div class="modal__close" @click="closeModal()"></div>

      <div class="form">

        <form class="add__form">
          <transition name="fade" mode="out-in">
            <Loading v-if="formLoading" />
            <div v-else class="add__content">
              <transition name="fade" mode="out-in">
                <div>
                  <div class="form__field">
                    <input type="text" class="input input--default" placeholder="E-mail"
                      @input="validateEmail($event, 0)" v-model="emailToEdit.email" :class="{
                        'input--error': emailToEdit.hasError,
                      }" />
                    <div class="form__error" v-if="emailToEdit.hasError">
                      {{ emailToEdit.errorPlaceholder }}
                    </div>
                  </div>

                  <div class="form__field">
                    <div class="company">
                      <label class="company__title" for="online_shipping">{{ t("editEmailModal.onlineShipping")
                        }}</label>
                      <div class="checkbox">
                        <input type="checkbox" id="online_shipping" class="checkbox__checkbox"
                          v-model="emailToEdit.online_shipping" />
                        <label for="online_shipping" class="checkbox__label"></label>
                      </div>
                    </div>
                  </div>

                  <div class="form__field">
                    <input type="text" id="contactPerson" class="input input--default"
                      :placeholder="t('editEmailModal.contactPerson')" v-model="emailToEdit.contact_person" />
                  </div>
                </div>
              </transition>
            </div>
          </transition>
        </form>
        <div v-if="isRequesting" class="modal__button modal__button--submit button button--default button--loading">
        </div>
        <button v-else type="button" class="modal__button modal__button--submit button button--default"
          @click="saveEmail">
          <span>{{ $t(`buttons.${'saveBtn'}`) }}</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import Loading from "@/partials/Loading.vue";
import api from "@/api";

defineOptions({
  name: "EditCompanyEmailModal"
});

const store = useStore();
// eslint-disable-next-line no-unused-vars
const { t } = useI18n();

// Reactive Data State
const formLoading = ref(false);
const isRequesting = ref(false);
const emailRegex = ref(/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/);

const emailToEdit = ref({
  id: null,
  email: "",
  hasError: false,
  errorPlaceholder: "",
  online_shipping: false,
  contact_person: "",
});

// Computed Properties
const customer = computed(() => store.getters.customer);
const emailIdToEdit = computed(() => store.getters.emailIdToEdit);

// Watchers - Load email data when modal opens
watch(emailIdToEdit, (newEmailId) => {
  if (newEmailId && customer.value && customer.value.customer_emails) {
    const foundEmail = customer.value.customer_emails.find(email => email.id === newEmailId);
    if (foundEmail) {
      emailToEdit.value = {
        id: foundEmail.id,
        email: foundEmail.email || "",
        hasError: false,
        errorPlaceholder: "",
        online_shipping: foundEmail.online_shipping == 1 ? true : false,
        contact_person: foundEmail.contact_person || "",
      };
    }
  }
}, { immediate: true });

// Methods
const closeModal = () => {
  store.commit("setShowEditCompanyEmailModal", false);
};

const updateCustomer = async () => {
  try {
    if (customer.value?.id) {
      const updatedCustomer = await store.dispatch("getGlobalCustomer", customer.value.id);
      store.commit("setCustomerDetails", updatedCustomer);
    }
  } catch (err) {
    console.error(err);
  }
};

const saveEmail = async () => {
  if (emailToEdit.value.hasError || !emailToEdit.value.email) {
    return;
  }

  isRequesting.value = true;
  const customer_emails = {
    email: emailToEdit.value.email,
    customer_id: customer.value.id,
    online_shipping: emailToEdit.value.online_shipping ? 1 : 0,
    contact_person: emailToEdit.value.contact_person,
  };

  try {
    await api({ requiresAuth: true }).put(
      `/v1/customer-emails/${emailToEdit.value.id}`,
      customer_emails
    );

    store.commit("setToast", {
      display: true,
      type: "success",
      message: t("editEmailModal.emailEditedSuccessfully"),
    });

    closeModal();
    isRequesting.value = false;
    await updateCustomer();
  } catch (err) {
    console.error(err);
    store.commit("setToast", {
      display: true,
      type: "error",
      message: err?.message || t("editEmailModal.somethingWentWrong"),
    });
    closeModal();
    isRequesting.value = false;
  }
};

const validateEmail = (event) => {
  const { value } = event.target;
  emailToEdit.value.email = value;

  if (
    emailToEdit.value.email &&
    !emailRegex.value.test(emailToEdit.value.email.toLowerCase())
  ) {
    emailToEdit.value.hasError = true;
    emailToEdit.value.errorPlaceholder = t("editEmailModal.invalidEmailAddress");
  } else {
    emailToEdit.value.hasError = false;
    emailToEdit.value.errorPlaceholder = "";
  }
};

</script>

<style lang="scss" scoped>
.company {
  display: flex;
  align-items: center;
  margin: 10px 0;

  &__title {
    color: var(--text-2);
    font-family: var(--muli-text-semi-bold);
    margin-right: 10px;
  }
}

.modal__panel {
  width: 500px;
  height: 500px;
}

.modal__main-icon {
  scale: 0.8;
}

.add__content {
  margin-top: 50px;
}
</style>